import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import type { Id } from '@contracts/common';
import type { BatchJobOptionFor, BatchOptionFor, BatchRowOptionsDTO } from '@contracts/ingest';
import { Combobox, type ComboOption } from '../../../components/ui/combobox';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { cn } from '../../../lib/utils';
import type { ApiClientError } from '../../../api/client';
import { useDebounced } from '../../partners/api';
import { jobOptions, rowOptions } from './api';

const MUTED = 'text-[hsl(var(--h-muted-foreground))]';
/** The "chưa chọn" choice's value: no record id is empty. */
const NONE = '';

/**
 * Where the list comes from: a row's options (what that row may point at), or the batch's own (D139: the mapping
 * bar's defaults, asked with no row, a category within its direction).
 */
export type PickFrom = { rowId: Id; of: BatchOptionFor } | { of: BatchJobOptionFor; direction?: 'in' | 'out' };

/**
 * A row's "Chọn …" (S5 §8.3; L3 P2, D108): up to 20 records the person sees, of a reference the row uses or of the
 * row's own kind for a new target, searched on the server as the person types. The list is asked for only once
 * the popover opens. Choosing one hands its id to `onPick`, which sends it as the row's `refs` or `targetId`.
 */
export function RefPicker({ jobId, from, label, onPick, onClear, disabled, className, children, name }: {
  jobId: Id;
  from: PickFrom;
  /** The field's and the list's name: the reason's own words ("Chọn dự án"). */
  label: string;
  /** The chosen record's id, and its name for a caller that shows it before a row carrying it is read. */
  onPick: (id: Id, label: string) => void;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
  /** The trigger's accessible name, when its words alone do not say what it picks (the mapping bar's defaults). */
  name?: string;
  /** D139: offers "chưa chọn" first, which clears the pick; only given when something is picked. */
  onClear?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const q = useDebounced(query.trim(), 250);
  const { data, error, isFetching } = useQuery<BatchRowOptionsDTO, ApiClientError>({
    queryKey: ['assistant', 'batch', jobId, 'options', 'rowId' in from ? from.rowId : null, from.of,
      'direction' in from ? from.direction ?? null : null, q],
    queryFn: () => ('rowId' in from ? rowOptions(jobId, from.rowId, from.of, q)
      : jobOptions(jobId, from.of, 'direction' in from ? from.direction : undefined, q)),
    enabled: open,
    staleTime: 30_000,
  });
  const options: ComboOption[] = [
    ...(onClear === undefined ? [] : [{ key: 'none', value: NONE, label: <i className={MUTED}>chưa chọn</i> }]),
    ...(data?.items ?? []).map((o) => ({
      key: o.id, value: o.id,
      label: <>{o.label}{o.hint !== null && <span className={MUTED}>{` · ${o.hint}`}</span>}</>,
    })),
  ];
  const empty = error != null ? error.message : isFetching ? 'Đang tìm…' : 'Không có bản ghi nào khớp';

  return (
    <Popover open={open} onOpenChange={(next) => { setOpen(next); if (!next) setQuery(''); }}>
      <PopoverTrigger asChild disabled={disabled}>
        <button type="button" aria-label={name}
          className={cn('inline-flex min-h-11 items-center gap-0.5 rounded-md text-left font-semibold text-[hsl(var(--h-primary))] underline decoration-dashed underline-offset-4 transition-colors hover:text-[hsl(var(--h-primary-hover))] disabled:cursor-default disabled:opacity-50 lg:min-h-0', className)}>
          {children}<ChevronDown className="size-3.5 shrink-0" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(18rem,calc(100vw-2rem))] p-2">
        <Combobox label={label} placeholder="Tìm…" query={query} onQueryChange={setQuery} options={options}
          empty={empty} autoFocus listClassName="max-h-60"
          onChoose={(id) => {
            setOpen(false);
            setQuery('');
            if (id === NONE) onClear?.();
            else onPick(id, data?.items.find((o) => o.id === id)?.label ?? '');
          }} />
      </PopoverContent>
    </Popover>
  );
}
